import { fireCmdbErrorAnalytics } from '@atlassian/jira-servicedesk-cmdb-error-handling/src/fireCmdbErrorAnalytics.tsx';
import { performPutRequest } from '@atlassian/jira-fetch/src/utils/requests.tsx';
import type {
	CmdbObjectResponse,
	CmdbObject,
} from '@atlassian/jira-servicedesk-insight-shared-types/src/common/types/cmdb-object.tsx';
import type { IssueKey } from '@atlassian/jira-shared-types/src/general.tsx';
import type { CmdbObjectFieldValues } from './types/index.tsx';

const baseKeys = [
	'id',
	'workspaceId',
	'objectId',
	'objectKey',
	'label',
	'avatar',
	'objectType',
	'links',
	'attributes',
];
const avatarKeys = ['url48'];
const objectTypeKeys = ['id', 'workspaceId', 'objectTypeId', 'name', 'icon', 'objectSchemaId'];
const objectTypeIconKeys = ['id', 'name', 'url16', 'url48'];
const isObject = (variable: unknown): variable is Record<string, unknown> =>
	typeof variable === 'object' && variable !== null;

const getMissingKeys = (object: unknown, keys: Array<string>) => {
	if (!isObject(object)) return keys;
	const missingKeys: Array<string> = [];
	keys.forEach((key) => {
		if (!Object.prototype.hasOwnProperty.call(object, key)) {
			missingKeys.push(key);
		}
	});
	return missingKeys;
};

/**
 * We unfortunately need this method, because the CMDB field only stores the IDs of the objects (check types/bento-field.js),
 * and when updating the field with new values, we only need to pass the object IDs, so we would like to keep
 * services/cmdb-object-field type as CmdbObjectFieldValues.
 * However, on initial load, we actually get passed the enriched CMDB object values, so we need some way to convert those
 * initially passed values into CmdbObject types to make flow happy.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const convertValueToCmdbObjectType = (values: any[]): CmdbObjectResponse[] => {
	const convertToType = (value: Partial<CmdbObjectResponse>): CmdbObjectResponse | undefined => {
		const missingKeys = [
			...getMissingKeys(value, baseKeys),
			...(value && value.avatar
				? getMissingKeys(value.avatar, avatarKeys).map((key) => `avatar.${key}`)
				: []),
			...(value && value.objectType
				? getMissingKeys(value.objectType, objectTypeKeys).map((key) => `objectType.${key}`)
				: []),
			...(value && value.objectType && value.objectType.icon
				? getMissingKeys(value.objectType.icon, objectTypeIconKeys).map(
						(key) => `objectType.icon.${key}`,
					)
				: []),
		];

		if (missingKeys.length === 0) {
			// @ts-expect-error - TS2322 - Type 'Partial<CmdbObjectResponse>' is not assignable to type 'CmdbObjectResponse'.
			return value;
		}

		fireCmdbErrorAnalytics({
			meta: {
				id: 'confirm.object.type',
				packageName: 'jiraIssueFieldCmdbObject',
				teamName: 'falcons',
			},
			error: new Error(`Object was missing required properties: ${missingKeys.join(', ')}`),
		});
		return undefined;
	};
	return values.map(convertToType).filter(Boolean);
};

export const sortCmdbObjects = (unsortedObjects: CmdbObject[]): CmdbObject[] =>
	unsortedObjects.sort((objectOne, objectTwo) => {
		if (objectOne.label.toLowerCase() < objectTwo.label.toLowerCase()) {
			return -1;
		}
		if (objectOne.label.toLowerCase() > objectTwo.label.toLowerCase()) {
			return 1;
		}
		return 0;
	});

const getFieldUpdate = (fieldKey: string, value: CmdbObjectFieldValues) => ({
	fields: {
		[fieldKey]: value,
	},
});

export const saveFieldCall = async (
	issueKey: IssueKey,
	fieldKey: string,
	newValue: CmdbObject[],
) => {
	const fieldValues = newValue.map((cmdObject) => ({ id: cmdObject.id }));

	return performPutRequest(`/rest/api/3/issue/${issueKey}/`, {
		body: JSON.stringify(getFieldUpdate(fieldKey, fieldValues)),
	});
};
